<template>
  <div ref="chartContainer" class="combined-chart-container">
    <chart-toolbar
      :locked="!!lockedY"
      :zoomed="!!zoomRange"
      :lockable="!!tkCurrentDomains"
      :exportable="!loading && !!tkCurrentDomains"
      @lock="tkToggleLockY(tkCurrentDomains)"
      @reset="tkPublishZoom(null)"
      @png="tkExportPng(fileName)"
      @csv="tkExportCsvNow()"
    />
    <div v-if="loading" class="chart-skeleton" :style="{ height: `${height}px` }" aria-busy="true">
      <slot name="loading"><span class="sr-only">Loading...</span></slot>
    </div>
    <div v-if="!loading && (datasets.length === 0 || data.length === 0)" class="no-data">
      <slot name="no-data">No data available</slot>
    </div>
    <div v-show="!loading" :id="chartId" class="chart-wrapper"></div>
    <div ref="tooltip" class="d3-tooltip"></div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { formatNum } from '@/util/number';
import { formatDateTime } from '@/util/date';
import { CHART_TOOLTIP } from '@/util/apiDatetime';
import chartToolkit from '@/components/charts/chartToolkit';
import ChartToolbar from '@/components/charts/ChartToolbar.vue';

export default {
  name: 'CombinedChart',
  mixins: [chartToolkit],
  components: { ChartToolbar },
  props: {
    chartId: { type: String, default: 'combined-chart' },
    data: { type: Array, default: () => [] },
    datasets: { type: Array, default: () => [] },
    timestampKey: { type: String, default: 'timestamp' },
    xFormat: { type: String, default: '%d %b %H:%M' },
    height: { type: Number, default: 300 },
    loading: { type: Boolean, default: false },
    // Left Y-axis configuration
    leftYLabel: { type: String, default: '' },
    leftYTickFormat: { type: String, default: '' },
    // Right Y-axis configuration
    rightYLabel: { type: String, default: '' },
    rightYTickFormat: { type: String, default: '' },
    // kanal chartSync sendiri
    syncGroup: { type: String, default: 'voyage' },
    fileName: { type: String, default: 'voyage-speed-wave' },
    // [startMs, endMs, unit] buat nama file export
    range: { type: Array, default: null },
  },
  data() {
    return {
      chart: null,
      resizeObserver: null,
      tkCurrentDomains: null,
    };
  },
  created() {
    // non-reaktif
    this.tkLast = null;
    this.drawFrame = 0;
    // state toolkit per chart per voyage
    this.tkKeyExtra = `${this.chartId}:${this.$route?.params?.id || ''}`;
  },
  watch: {
    datasets: {
      handler() {
        this.drawChart();
      },
      deep: true,
    },
    data: {
      handler() {
        this.drawChart();
      },
      deep: true,
    },
    loading() {
      if (!this.loading) this.drawChart();
    },
  },
  mounted() {
    this.tkBind();
    if (!this.loading) this.drawChart();
    this.resizeObserver = new ResizeObserver(() => this.scheduleDraw());
    this.resizeObserver.observe(this.$refs.chartContainer);
  },
  beforeDestroy() {
    this.tkUnbind();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    cancelAnimationFrame(this.drawFrame);
    this.destroyChart();
  },
  methods: {
    scheduleDraw() {
      cancelAnimationFrame(this.drawFrame);
      this.drawFrame = requestAnimationFrame(() => {
        if (!this.loading) this.drawChart();
      });
    },
    tkFileRange() {
      return this.range && this.range.length >= 2 ? this.range : null;
    },
    tkExportCsvNow() {
      if (!this.tkLast) return;
      this.tkExportCsv(this.fileName, this.tkLast.timestamps, this.tkLast.datasets);
    },
    drawChart() {
      if (!this.$refs.chartContainer || !this.datasets.length || !this.data.length) {
        d3.select(`#${this.chartId}`).selectAll('*').remove();
        this.tkLast = null;
        this.tkCurrentDomains = null;
        return;
      }

      const container = this.$refs.chartContainer;
      const tooltip = this.$refs.tooltip;
      d3.select(`#${this.chartId}`).selectAll('*').remove();
      d3.select(tooltip).selectAll('*').remove();

      const compact = container.clientWidth < 576;
      const margin = compact
        ? {
          top: 20, right: 52, bottom: 50, left: 48,
        }
        : {
          top: 20, right: 120, bottom: 50, left: 60,
        };
      const width = container.clientWidth - margin.left - margin.right;
      if (width <= 0) return;
      const height = this.height - margin.top - margin.bottom;

      const svg = d3.select(`#${this.chartId}`)
        .append('svg')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      // clip area data
      const clipId = `tk-clip-${this._uid}`;
      svg.append('defs').append('clipPath').attr('id', clipId)
        .append('rect')
        .attr('width', width)
        .attr('height', height);
      const dataLayer = svg.append('g').attr('clip-path', `url(#${clipId})`);

      const series = this.datasets.map((ds) => {
        const values = this.data.map((d) => (d[ds.dataKey] === undefined ? null : d[ds.dataKey]));
        return {
          ...ds,
          axis: ds.type === 'bar' ? 'right' : 'left',
          data: values,
          interpolated: values,
        };
      });
      const visible = this.tkVisible(series);
      const lineSeries = visible.filter((ds) => ds.axis === 'left');
      const barSeries = visible.filter((ds) => ds.axis === 'right');

      // Extract timestamps
      const timestamps = this.data.map((d) => new Date(d[this.timestampKey] * 1000));
      this.tkLast = { timestamps, datasets: visible };

      // X scale (time)
      const x = d3.scaleTime()
        .domain(this.tkXDomain(timestamps))
        .range([0, width]);

      // domain mentah buat kunci Y
      const [leftDomain, rightDomain] = this.tkAlignZero([
        this.tkDomain('left', visible, 10),
        this.tkDomain('right', visible, 1),
      ]);
      this.tkCurrentDomains = { left: leftDomain, right: rightDomain };

      // Y scale for line (left)
      const yLine = d3.scaleLinear()
        .domain([leftDomain[0], Math.ceil(leftDomain[1] / 5) * 5 + 5])
        .range([height, 0]);

      // Y scale for bars (right)
      const yBar = d3.scaleLinear()
        .domain([rightDomain[0], (Math.ceil(rightDomain[1] * 10) / 10) + 0.5])
        .range([height, 0]);
      const getScale = (axisName) => (axisName === 'left' ? yLine : yBar);

      // Add X axis
      svg.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).ticks(compact ? 4 : 10).tickFormat(d3.timeFormat(this.xFormat)))
        .selectAll('text')
        .attr('transform', 'rotate(-45)')
        .style('text-anchor', 'end')
        .attr('dx', '-0.5em')
        .attr('dy', '0.5em')
        .attr('fill', this.tkChrome.muted);

      // Add left Y axis (line)
      const leftYFormat = this.leftYTickFormat || ((d) => d);
      svg.append('g')
        .call(d3.axisLeft(yLine).tickFormat(leftYFormat))
        .selectAll('text')
        .attr('fill', this.tkChrome.muted);

      // Left Y axis label
      if (this.leftYLabel) {
        svg.append('text')
          .attr('transform', 'rotate(-90)')
          .attr('y', compact ? -36 : -45)
          .attr('x', -height / 2)
          .attr('text-anchor', 'middle')
          .attr('fill', series.find((ds) => ds.axis === 'left')?.color || '#0d6efd')
          .attr('font-size', '12px')
          .text(this.leftYLabel);
      }

      // Add right Y axis (bar)
      if (barSeries.length > 0) {
        const rightYFormat = this.rightYTickFormat || ((d) => d);
        svg.append('g')
          .attr('transform', `translate(${width},0)`)
          .call(d3.axisRight(yBar).tickFormat(rightYFormat))
          .selectAll('text')
          .attr('fill', this.tkChrome.muted);

        // Right Y axis label
        if (this.rightYLabel) {
          svg.append('text')
            .attr('transform', 'rotate(90)')
            .attr('y', -(width + (compact ? 44 : 65)))
            .attr('x', height / 2)
            .attr('dy', '.5em')
            .attr('text-anchor', 'middle')
            .attr('fill', series.find((ds) => ds.axis === 'right')?.color || '#0dcaf0')
            .attr('font-size', '12px')
            .text(this.rightYLabel);
        }
      }

      // Grid lines
      svg.append('g')
        .attr('class', 'grid')
        .attr('opacity', 0.1)
        .call(d3.axisLeft(yLine).tickSize(-width).tickFormat(''));

      // Draw line datasets
      lineSeries.forEach((item) => {
        const seriesData = item.data;

        dataLayer.append('path')
          .datum(seriesData)
          .attr('fill', 'none')
          .attr('stroke', item.color)
          .attr('stroke-width', 2.5)
          .attr('d', d3.line()
            .defined((d) => d !== null && d !== undefined)
            .x((d, i) => x(timestamps[i]))
            .y((d) => yLine(d)));

        // Dots
        dataLayer.selectAll(`.dot-${item.name.replace(/\s+/g, '-')}`)
          .data(seriesData.map((d, i) => ({ value: d, date: timestamps[i] }))
            .filter((d) => d.value !== null && d.value !== undefined))
          .enter()
          .append('circle')
          .attr('class', `dot-${item.name.replace(/\s+/g, '-')}`)
          .attr('cx', (d) => x(d.date))
          .attr('cy', (d) => yLine(d.value))
          .attr('r', 3)
          .attr('fill', item.color)
          .style('pointer-events', 'none');
      });

      // Draw bar datasets
      if (barSeries.length > 0) {
        const [domainStart, domainEnd] = x.domain();
        const inView = timestamps.filter((t) => t >= domainStart && t <= domainEnd).length;
        const barWidth = (width / Math.max(1, inView)) * 0.7;
        const barSet = barSeries[0]; // Support single bar dataset for now

        dataLayer.selectAll('.bar')
          .data(barSet.data.map((d, i) => ({ value: d, date: timestamps[i] }))
            .filter((d) => d.value !== null && d.value !== undefined))
          .enter()
          .append('rect')
          .attr('class', 'bar')
          .attr('x', (d) => x(d.date) - barWidth / 2)
          .attr('y', (d) => yBar(d.value))
          .attr('width', barWidth)
          .attr('height', (d) => height - yBar(d.value))
          .attr('fill', `rgba(${this.hexToRgb(barSet.color)}, 0.3)`)
          .attr('stroke', barSet.color)
          .attr('stroke-width', 1);
      }

      // Focus group for hover
      const focusGroup = svg.append('g').style('display', 'none');
      const focusLine = focusGroup.append('line')
        .attr('stroke', '#aaa')
        .attr('stroke-dasharray', '3 3')
        .attr('y1', 0)
        .attr('y2', height);

      const focusCircles = visible.map((ds) => focusGroup.append('circle')
        .attr('r', 5)
        .attr('fill', ds.color)
        .attr('stroke', '#fff')
        .attr('stroke-width', 2));

      this.tkOnHover((ms) => {
        const [first, last] = x.domain();
        if (ms === null || ms < +first || ms > +last) {
          focusGroup.style('display', 'none');
          return;
        }
        focusGroup.style('display', null);
        focusLine.attr('x1', x(new Date(ms))).attr('x2', x(new Date(ms)));
      });

      // Legend with click toggle
      const legendItemWidth = 100;
      const legendColumns = Math.max(1, Math.floor(width / legendItemWidth));
      const legendGroup = svg.append('g')
        .attr('class', 'chart-legend')
        .attr('transform', `translate(${Math.max(0, (width - Math.min(this.datasets.length, legendColumns) * legendItemWidth) / 2)}, -10)`);

      const legendItem = legendGroup.selectAll('g')
        .data(this.datasets)
        .enter()
        .append('g')
        .attr('transform', (d, i) => {
          const col = i % legendColumns;
          const row = Math.floor(i / legendColumns);
          return `translate(${col * legendItemWidth}, ${row * 18})`;
        })
        .style('cursor', 'pointer');

      legendItem.append('rect')
        .attr('width', 12)
        .attr('height', 12)
        .attr('y', -10)
        .attr('rx', 2)
        .attr('ry', 2)
        .attr('fill', (d) => d.color);

      legendItem.append('text')
        .attr('x', 18)
        .attr('y', 0)
        .attr('dy', '0.32em')
        .style('font-size', '11px')
        .attr('fill', this.tkChrome.text)
        .text((d) => d.name);

      legendItem.on('click', (event, d) => {
        this.tkToggleSeries(d.name);
      });
      legendItem.select('rect').attr('opacity', (d) => (this.hiddenSeries[d.name] ? 0.3 : 1));
      legendItem.select('text')
        .attr('opacity', (d) => (this.hiddenSeries[d.name] ? 0.4 : 1))
        .style('text-decoration', (d) => (this.hiddenSeries[d.name] ? 'line-through' : 'none'));

      // Hover interaction
      const bisect = d3.bisector((d) => d).left;

      // hover lewat overlay brush
      const hoverBase = this.tkInstallBrush(svg, x, width, height);
      hoverBase
        .on('mouseover', () => {
          d3.select(tooltip).style('opacity', 1);
          focusGroup.style('display', null);
        })
        .on('mouseout', () => {
          d3.select(tooltip).style('opacity', 0);
          focusGroup.style('display', 'none');
          this.tkPublishHover(null);
        })
        .on('mousemove', (event) => {
          const x0 = x.invert(d3.pointer(event)[0]);
          const i = bisect(timestamps, x0, 1);
          let index = 0;
          if (i < timestamps.length && i > 0) {
            index = (x0 - timestamps[i - 1] > timestamps[i] - x0) ? i : i - 1;
          }
          const date = timestamps[index];
          const xPos = x(date);

          focusLine.attr('x1', xPos).attr('x2', xPos);
          this.tkPublishHover(+date);

          visible.forEach((ds, j) => {
            const val = ds.data[index];
            if (val !== null && val !== undefined) {
              focusCircles[j].attr('cx', xPos).attr('cy', getScale(ds.axis)(val)).style('display', null);
            } else {
              focusCircles[j].style('display', 'none');
            }
          });

          let html = `<strong>${formatDateTime(date, CHART_TOOLTIP)}</strong><br/>`;
          visible.forEach((ds) => {
            const val = ds.data[index];
            if (val !== null && val !== undefined) {
              html += `<div style="display: flex; align-items: center; gap: 5px;">
                        <span style="width: 10px; height: 10px; border-radius: 50%; background:${ds.color}; display: inline-block;"></span>
                        ${ds.name}: ${typeof val === 'number' ? formatNum(val, ds.type === 'bar' ? 2 : 1) : val}
                      </div>`;
            }
          });

          const [posX, posY] = d3.pointer(event, container);
          let left = posX + 15;
          if (left + 160 > width) left = posX - 170;

          d3.select(tooltip)
            .html(html)
            .style('left', `${left}px`)
            .style('top', `${posY + 15}px`);
        });

      this.chart = svg;
    },
    destroyChart() {
      if (this.$refs.chartContainer) {
        d3.select(`#${this.chartId}`).selectAll('*').remove();
      }
      this.chart = null;
    },
    hexToRgb(hex) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
        : '200, 200, 200';
    },
  },
};
</script>

<style scoped>
.combined-chart-container {
  position: relative;
  width: 100%;
}

.chart-wrapper {
  width: 100%;
  min-height: 220px;
}

.chart-wrapper svg {
  max-width: 100%;
  overflow: visible;
}

.chart-wrapper .domain {
  stroke: var(--mz-border);
  stroke-width: 1;
}

.chart-wrapper .tick line {
  stroke: var(--mz-border);
}

.chart-wrapper .tick text {
  font-size: 11px;
}

.chart-skeleton {
  width: 100%;
  border-radius: 0.375rem;
  background: linear-gradient(90deg, var(--mz-skeleton-base) 25%, var(--mz-skeleton-highlight) 37%, var(--mz-skeleton-base) 63%);
  background-size: 400% 100%;
  animation: chart-skeleton-shimmer 1.4s ease infinite;
}

@keyframes chart-skeleton-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

.no-data {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 220px;
  color: var(--mz-text-subtle);
  font-size: var(--mz-fs-base);
}

.d3-tooltip {
  position: absolute;
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-family: inherit;
  z-index: 100;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;
  max-width: 200px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.d3-tooltip strong {
  display: block;
  margin-bottom: 6px;
  font-size: 11px;
  color: #e9ecef;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding-bottom: 4px;
}

.d3-tooltip > div {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 3px 0;
}

.chart-legend text {
  font-family: inherit;
}

.chart-legend rect {
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.grid line {
  stroke: var(--mz-border);
  stroke-dasharray: 2, 2;
}

.bar {
  transition: opacity 0.2s ease;
}

.bar:hover {
  opacity: 0.8;
}
</style>
